import React, {
    useCallback, useMemo, useState
} from "react";
import { useIntl, defineMessage, FormattedMessage } from "react-intl";
import { Controller, Control } from "react-hook-form";
import { Button } from "@smartling-ui/button";
import { ExternalLink } from "@smartling-ui/link";
import { Field } from "@smartling-ui/field";
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupButton,
    InputGroupText
} from "@smartling-ui/input-group";
import { Tooltip } from "@smartling-ui/tooltip";
import { User, Mail, Lock, Eye, EyeOff } from "lucide-react";

import { CompanyNameField } from "../CompanyNameField";
import { JobTitleField } from "../JobTitleField";
import { AcceptTermsField } from "../AcceptTermsField";
import { ServerErrorMessage } from "../ServerErrorMessage";
import { RegistrationFormType } from "../../types";
import { CONTACT_SALES_URL } from "../../constants";
import { validateUniqEmail } from "../../api/validateUniqEmail";
import validationMessages from "../../validationMessages";

import messages from "./messages";
import * as styles from "./styles.module.css";

const emailBlockedDomainMessage = defineMessage({
    description: "Email domain is in blocked list with contact link",
    defaultMessage: "Domain not accepted. Please <a>contact our team</a> to request a personalized demo."
});

function ContactTeamAnchor(chunks: React.ReactNode[]) {
    return (
        <ExternalLink
            href={CONTACT_SALES_URL}
            size="inherit"
            weight="inherit"
            color="inherit"
            className={styles.contactSalesLink}
        >
            {chunks}
        </ExternalLink>
    );
}

type Props = {
    loading: boolean;
    control: Control<RegistrationFormType>;
    hasDeveloperSandbox: boolean;
}

export function RegistrationForm({
    loading,
    control,
    hasDeveloperSandbox
}: Props) {
    const intl = useIntl();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const toggleShowPassword = useCallback(
        () => setShowPassword(prev => !prev),
        []
    );

    const toggleShowConfirmPassword = useCallback(
        () => setShowConfirmPassword(prev => !prev),
        []
    );

    const emailRules = useMemo(() => ({
        validate: {
            uniqueEmail: async (email: string) => {
                if (!email) return true;

                try {
                    const isUnique = await validateUniqEmail(email);

                    return isUnique || intl.formatMessage(
                        validationMessages.emailRegistered
                    );
                } catch {
                    return true;
                }
            }
        }
    }), [intl]);

    const passwordTooltip = intl.formatMessage(
        showPassword ? messages.hidePassword : messages.showPassword
    );
    const confirmPasswordTooltip = intl.formatMessage(
        showConfirmPassword
            ? messages.hidePassword
            : messages.showPassword
    );

    return (
        <div className={styles.form}>
            <Controller
                name="accountName"
                control={control}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field, fieldState }) => (
                    <Field.Root invalid={fieldState.invalid || !!fieldState.error}>
                        <Field.Label>
                            {intl.formatMessage(messages.name)}
                        </Field.Label>
                        <InputGroup>
                            <InputGroupAddon align="inline-start">
                                <InputGroupText><User /></InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                data-testid="accountName"
                                autoFocus
                                autoComplete="name"
                                placeholder={intl.formatMessage(
                                    messages.namePlaceholder
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                name={field.name}
                            />
                        </InputGroup>
                        {fieldState.error && (
                            <Field.Error>
                                <ServerErrorMessage error={fieldState.error} />
                            </Field.Error>
                        )}
                    </Field.Root>
                )}
            />
            {hasDeveloperSandbox && (
                <Controller
                    name="companyName"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field, fieldState }) => (
                        <CompanyNameField
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            name={field.name}
                            error={fieldState.error}
                        />
                    )}
                />
            )}
            {hasDeveloperSandbox && (
                <Controller
                    name="jobTitle"
                    control={control}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={({ field, fieldState }) => (
                        <JobTitleField
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                            name={field.name}
                            error={fieldState.error}
                        />
                    )}
                />
            )}
            <Controller
                name="email"
                control={control}
                rules={emailRules}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field, fieldState }) => (
                    <Field.Root invalid={fieldState.invalid || !!fieldState.error}>
                        <Field.Label>
                            {intl.formatMessage(messages.email)}
                        </Field.Label>
                        <InputGroup>
                            <InputGroupAddon align="inline-start">
                                <InputGroupText><Mail /></InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                data-testid="email"
                                type="email"
                                autoComplete="email"
                                placeholder={intl.formatMessage(
                                    messages.emailPlaceholder
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                name={field.name}
                            />
                        </InputGroup>
                        {fieldState.error && (
                            <Field.Error>
                                {fieldState.error.type === "blockedDomain" ? (
                                    <FormattedMessage
                                        {...emailBlockedDomainMessage}
                                        values={{ a: ContactTeamAnchor }}
                                    />
                                ) : (
                                    <ServerErrorMessage error={fieldState.error} />
                                )}
                            </Field.Error>
                        )}
                    </Field.Root>
                )}
            />
            <Controller
                name="password"
                control={control}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field, fieldState }) => (
                    <Field.Root invalid={fieldState.invalid || !!fieldState.error}>
                        <Field.Label>
                            {intl.formatMessage(messages.password)}
                        </Field.Label>
                        <InputGroup>
                            <InputGroupAddon align="inline-start">
                                <InputGroupText><Lock /></InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                data-testid="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                placeholder={intl.formatMessage(
                                    messages.passwordPlaceholder
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                name={field.name}
                            />
                            <Tooltip content={passwordTooltip}>
                                <InputGroupButton
                                    type="button"
                                    layout="icon"
                                    aria-label={passwordTooltip}
                                    onClick={toggleShowPassword}
                                >
                                    {showPassword
                                        ? <EyeOff />
                                        : <Eye />}
                                </InputGroupButton>
                            </Tooltip>
                        </InputGroup>
                        {fieldState.error && (
                            <Field.Error>
                                <ServerErrorMessage error={fieldState.error} />
                            </Field.Error>
                        )}
                    </Field.Root>
                )}
            />
            <Controller
                name="confirmPassword"
                control={control}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field, fieldState }) => (
                    <Field.Root invalid={fieldState.invalid || !!fieldState.error}>
                        <Field.Label>
                            {intl.formatMessage(messages.confirmPassword)}
                        </Field.Label>
                        <InputGroup>
                            <InputGroupAddon align="inline-start">
                                <InputGroupText><Lock /></InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                                data-testid="confirmPassword"
                                type={
                                    showConfirmPassword
                                        ? "text"
                                        : "password"
                                }
                                autoComplete="new-password"
                                placeholder={intl.formatMessage(
                                    messages.confirmPasswordPlaceholder
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                ref={field.ref}
                                name={field.name}
                            />
                            <Tooltip content={confirmPasswordTooltip}>
                                <InputGroupButton
                                    type="button"
                                    layout="icon"
                                    aria-label={confirmPasswordTooltip}
                                    onClick={toggleShowConfirmPassword}
                                >
                                    {showConfirmPassword
                                        ? <EyeOff />
                                        : <Eye />}
                                </InputGroupButton>
                            </Tooltip>
                        </InputGroup>
                        {fieldState.error && (
                            <Field.Error>
                                <ServerErrorMessage error={fieldState.error} />
                            </Field.Error>
                        )}
                    </Field.Root>
                )}
            />

            <Controller
                name="acceptPrivacyPolicy"
                control={control}
                // eslint-disable-next-line react/jsx-no-bind
                render={({ field: rhfField, fieldState }) => (
                    <AcceptTermsField
                        checked={rhfField.value === true}
                        onChange={rhfField.onChange}
                        onBlur={rhfField.onBlur}
                        error={fieldState.error?.message}
                    />
                )}
            />

            <Button
                variant="primary"
                type="submit"
                size="lg"
                fullWidth
                disabled={loading}
            >
                {intl.formatMessage(messages.buttonSignUp)}
            </Button>
        </div>
    );
}
